import { computed, EventEmitter, signal } from "@angular/core";
import { FormControl } from "@angular/forms";
import { ParamMap } from "@angular/router";
import { FileModel } from "../hickory-api";
import { ApiConfig } from "../shared/api-config/api-config";
import { areEqual } from "../shared/model/utility";
import { FilesListRequest, FilesSearchAndSort } from "./files-datasource";

export class FileSelectedEvent {
  constructor(public file: FileModel, public viewContext: FilesViewParams) { }
}

export class FileDeleteJob {
  deleted = signal(0);
  failed = signal<string[]>([]);
  progress = computed(() => {
    return this.total > 0 ? ((this.deleted() + (this.failed?.length ?? 0)) / this.total) * 100 : 0;
  });

  constructor(public total: number) { }

  public onDeleteSuccess() {
    this.deleted.update(c => c + 1);
  }

  public onDeleteFailed(fileId: string) {
    this.failed.update(f => [...f, fileId]);
  }
}

export type FilesViewDisplayType = 'table' | 'details';

export class FilesViewConstants {
  public static readonly RTE_ROUTE = 'file-picker';
  public static readonly MANAGE_TABLE_ROUTE = 'files/management';
  public static readonly MANAGE_DETAILS_ROUTE = 'files/management/details';

  public static readonly TENANT_ID_PARAM = 'tenantId';
  public static readonly FILE_ID_PARAM = 'fileId';
  public static readonly FILE_TYPE_PARAM = 'fileTypes';
  public static readonly CK_EDITOR_FUNC_NUM_PARAM = 'CKEditorFuncNum';
  public static readonly DISPLAY_TYPE_PARAM = 'displayType';
  public static readonly SEARCH_TERM_PARAM = 'searchTerm';
  public static readonly SORT_FIELD_PARAM = 'sortField';
  public static readonly SORT_DIRECTION_PARAM = 'sortDirection';
  public static readonly SORT_COMBINED = 'sort';

  public static readonly SAVE_BUTTON = {
    label: 'Files.Shared.SaveFile',
    tip: 'Files.Shared.SaveFile',
    icon: 'save',
    color: 'primary'
  }

  public static readonly SELECT_BUTTON = {
    label: 'Files.Picker.UseFile',
    tip: 'Files.Picker.UseFile',
    icon: 'check_circle',
    color: 'primary'
  }

  public static readonly UPLOAD_BUTTON = {
    label: 'Files.Shared.UploadFile',
    tip: 'Files.Shared.UploadFile',
    icon: 'upload',
    color: 'primary'
  }

  public static readonly CANCEL_UPLOAD_BUTTON = {
    label: 'Files.Shared.CancelUpload',
    tip: 'Files.Shared.CancelUpload',
    icon: 'cancel',
    color: ''
  }

  public static readonly DELETE_BUTTON = {
    label: 'Files.Management.Delete',
    tip: 'Files.Management.Delete',
    icon: 'delete',
    color: 'warn'
  }
}

export interface FilesViewParams extends FilesSearchAndSort {
  tenantId?: number;
  fileId?: string;
  fileTypes?: 'image' | 'audio';
  displayType?: FilesViewDisplayType;
  functionNumber?: number;
}

export class FilesViewContext {

  // Indicates if the view is for the rich text editor
  public readonly isRTEView = signal<boolean>(false);
  public readonly isDialogView = signal<boolean>(false);
  public readonly isManagementView = signal<boolean>(false);
  public readonly allowMultiSelect = signal<boolean>(false);
  public readonly title = signal<string | null>('Files.Picker.Title');
  public readonly tenantId = signal<number>(null);
  public readonly fileId = signal<string | null>(null);
  public readonly fileTypes = signal<'image' | 'audio' | null>('image');
  public readonly displayType = signal<FilesViewDisplayType>('details');
  public readonly functionNumber = signal<number | null>(null);
  public readonly searchTerm = signal<string | null>(null);
  public readonly sortField = signal<keyof FileModel | null>(null);
  public readonly defaultSortField = 'FileName' as keyof FileModel;
  public readonly sortDirection = signal<'asc' | 'desc' | null>(null);

  public readonly deleteSelected = new EventEmitter<void>();
  public readonly useFile = new EventEmitter<void>();
  public readonly fileSelected = new EventEmitter<FileModel>();
  public readonly saveFile = new EventEmitter<void>();
  public readonly uploadConfirmed = new EventEmitter<void>();
  public readonly uploadCancelled = new EventEmitter<void>();
  public readonly searchFiles = new EventEmitter<string>();
  public readonly clearSearch = new EventEmitter<void>();
  public readonly sortChanged = new EventEmitter<{ field: keyof FileModel | null, direction: 'asc' | 'desc' | null }>();
  public readonly tenantChanged = new EventEmitter<number | null>();
  public readonly updateQueryParams = new EventEmitter<void>();

  public readonly saveButton = FilesViewConstants.SAVE_BUTTON;
  public readonly selectButton = FilesViewConstants.SELECT_BUTTON;
  public readonly uploadButton = FilesViewConstants.UPLOAD_BUTTON;

  constructor(readonly params: ParamMap, readonly currentRoute: string, private readonly isSuperAdmin: boolean) {
    this.updateFromParams(params, currentRoute);
  }

  public shouldReload(prev: FilesViewParams): boolean {
    const getReloadParams = (p: FilesViewParams) => ({
      tenantId: p.tenantId,
      fileTypes: p.fileTypes,
      displayType: p.displayType,
      searchTerm: p.searchTerm
    });
    const current = this.toInterface();
    return !areEqual(getReloadParams(current), getReloadParams(prev));
  }

  public update(data: FilesViewParams): void {
    this.fileId.set(data.fileId ?? this.fileId());
    this.fileTypes.set(data.fileTypes ?? this.fileTypes());
    this.functionNumber.set(data.functionNumber ?? this.functionNumber());
    this.searchTerm.set(data.searchTerm ?? this.searchTerm());
    this.sortField.set(data.sortField ?? this.sortField());
    this.sortDirection.set(data.sortDirection ?? this.sortDirection());
    this.displayType.set(data.displayType ?? this.displayType());
    
    if (data.tenantId !== undefined && this.isSuperAdmin) {
      this.tenantId.set(data.tenantId);
    }

    this.updateQueryParams.emit();
  }

  public updateFromParams(params: ParamMap, currentRoute: string): void {
    this.isManagementView.set([
      'files',
      FilesViewConstants.MANAGE_TABLE_ROUTE,
      FilesViewConstants.MANAGE_DETAILS_ROUTE
    ].some(r => currentRoute.endsWith(r)));
    this.isRTEView.set(currentRoute.endsWith(FilesViewConstants.RTE_ROUTE));
    this.isDialogView.set(!this.isRTEView() && !this.isManagementView());

    if (params.has(FilesViewConstants.CK_EDITOR_FUNC_NUM_PARAM)) {
      const funcNum = +params.get(FilesViewConstants.CK_EDITOR_FUNC_NUM_PARAM)!;
      this.functionNumber.set(funcNum);
    }

    const fileType = params.get(FilesViewConstants.FILE_TYPE_PARAM);
    if (fileType === 'image' || fileType === 'audio' || fileType === null) {
      this.fileTypes.set(fileType as 'image' | 'audio' | null);
    }
    if (!this.isManagementView() && !fileType) {
      this.fileTypes.set('image');
    }

    if (this.isManagementView()) {
      this.handleParamsForManagement(params);
    } else {
      this.title.set('Files.Picker.Title');
      this.tenantId.set(+ApiConfig.tenantId);
      this.displayType.set('details');
    }

    this.handleSearchAndSortParams(params);
  }

  private handleSearchAndSortParams(params: ParamMap) {
    this.searchTerm.set(params.get(FilesViewConstants.SEARCH_TERM_PARAM) ?? null);
    let sortField: keyof FileModel | null = this.defaultSortField;
    let sortDirection: 'asc' | 'desc' = 'asc';
    if (params.has(FilesViewConstants.SORT_COMBINED)) {
      const querySort = params.get(FilesViewConstants.SORT_COMBINED);
      if (querySort?.at(0) === '-') {
        sortDirection = 'desc';
        sortField = querySort?.substring(1) as keyof FileModel;
      }
      else {
        sortField = querySort as keyof FileModel;
      }
    }
    this.sortField.set(sortField);
    this.sortDirection.set(sortDirection);
  }

  private handleParamsForManagement(params: ParamMap) {
    this.title.set('');
    if (this.isSuperAdmin && params.has(FilesViewConstants.TENANT_ID_PARAM)) {
      this.tenantId.set(+params.get(FilesViewConstants.TENANT_ID_PARAM)!);
    } else {
      this.tenantId.set(+ApiConfig.tenantId);
    }

    this.fileId.set(params.get(FilesViewConstants.FILE_ID_PARAM));

    const displayType = params.get(FilesViewConstants.DISPLAY_TYPE_PARAM);
    this.displayType.set((displayType as 'table' | 'details') ?? 'details');
    this.allowMultiSelect.set(this.displayType() === 'table');
  }

  public toInterface(): FilesViewParams {
    return {
      tenantId: this.tenantId(),
      fileId: this.fileId(),
      fileTypes: this.fileTypes(),
      displayType: this.displayType(),
      functionNumber: this.functionNumber(),
      searchTerm: this.searchTerm(),
      sortField: this.sortField(),
      sortDirection: this.sortDirection()
    }
  }

  public toFileListRequest(): FilesListRequest {
    return {
      includeAudioUrls: false,
      ...this.toInterface(),
      filterType: this.fileTypes(),
    } as FilesListRequest;
  }

  public toQueryParams(changes: Partial<FilesViewParams> = {}): Record<string, unknown> {
    const current = this.toInterface();
    const params = { ...current, ...changes };
    params.sort = `${params.sortDirection == 'desc' ? '-' : ''}${params.sortField ?? this.defaultSortField}`;
    delete params.sortDirection;
    delete params.sortField;
    return params;
  }

  public getSearchSuffix(searchControl: FormControl<string>) {
    if (!this.searchTerm() || !searchControl?.value || this.searchTerm() !== searchControl.value) {
      return {
        icon: 'search',
        label: 'Files.Shared.SearchFiles',
        hint: 'Files.Shared.SearchFilesTip',
        disabled: searchControl.invalid || !searchControl.value,
        action: () => this.searchFiles.emit()
      };
    }

    if (searchControl.value === this.searchTerm()) {
      return {
        icon: 'clear',
        label: 'Files.Shared.ClearSearch',
        hint: 'Files.Shared.ClearSearchTip',
        disabled: false,
        action: () => this.clearSearch.emit()
      };
    }
  }
}
